import React, { useMemo, useState } from 'react';
import {
  Box,
  TextInput,
  Group,
  Text,
  Checkbox,
  Badge,
  Stack,
  Divider,
  ActionIcon,
  Tooltip,
  Modal,
  Button,
} from '@mantine/core';
import { IconSearch, IconCopy, IconLayersLinked, IconSettings } from '@tabler/icons-react';
import { BaseSchema, TemplateDoc, RecordWithMetadata } from '../../../types/geneva-schema';
import { resolveFieldOverridesRecursively } from '../../../utils/geneva-schema-helpers';
import { TemplateValidationEditor } from './TemplateValidationEditor';

interface ValidationTemplate {
  expr: string;
  async?: boolean;
  params?: string[];
}

interface GenevaRecordsListProps {
  schema: BaseSchema | null;
  template: TemplateDoc | null;
  selectedRecord: string | null;
  onRecordSelect: (recordName: string) => void;
  onRecordToggle: (recordName: string, enabled: boolean) => void;
  onRecordLookupFieldsChange: (recordName: string, fields: Array<{ name: string; alias?: string }>) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  validationTemplates?: Record<string, ValidationTemplate>;
}

export const GenevaRecordsList: React.FC<GenevaRecordsListProps> = ({
  schema,
  template,
  selectedRecord,
  onRecordSelect,
  onRecordToggle,
  onRecordLookupFieldsChange,
  searchQuery,
  onSearchChange,
  validationTemplates = {},
}) => {
  const [lookupFieldsModal, setLookupFieldsModal] = useState<{ open: boolean; recordName: string }>({ open: false, recordName: '' });
  const [recordValidationModal, setRecordValidationModal] = useState<{ open: boolean; recordName: string }>({ open: false, recordName: '' });
  const [pendingValidationRules, setPendingValidationRules] = useState<any>(undefined);
  const [editingFields, setEditingFields] = useState<Array<{ name: string; alias?: string }>>([]);
  const recordsWithMetadata = useMemo((): RecordWithMetadata[] => {
    if (!schema || !template) return [];

    // Real records from schema
    const realRecords = schema.records.map(record => {
      const enabled = template.enabledRecords[record.record] || false;
      const fieldOverrides = template.fieldOverrides[record.record] || {};
      const overriddenFieldCount = Object.keys(fieldOverrides).length;

      return {
        ...record,
        enabled,
        fieldCount: record.fields.length,
        overriddenFieldCount,
        isVirtual: false,
      };
    });

    // Virtual records from template
    console.log('Processing virtual records:', template.virtualRecords);
    const virtualRecords = Object.entries(template.virtualRecords || {}).map(([recordId, virtualRecord]) => {
      console.log(`Processing virtual record ${recordId}:`, virtualRecord);
      // Handle multi-record virtual records
      if (virtualRecord.isMultiRecord && virtualRecord.baseRecords) {
        // For multi-record, aggregate fields from all base records
        const allFields: any[] = [];
        let totalFieldCount = 0;

        virtualRecord.baseRecords.forEach(baseRecordName => {
          const baseRecord = schema.records.find(r => r.record === baseRecordName);
          if (baseRecord) {
            allFields.push(...baseRecord.fields);
            totalFieldCount += baseRecord.fields.length;
          }
        });

        // Count overrides from multi-record config
        let overriddenFieldCount = 0;
        if (virtualRecord.multiRecordConfig) {
          virtualRecord.multiRecordConfig.forEach(config => {
            if (config.fieldOverrides) {
              overriddenFieldCount += Object.keys(config.fieldOverrides).length;
            }
          });
        }

        return {
          record: recordId,
          fields: allFields, // Combined fields from all base records
          enabled: virtualRecord.enabled,
          fieldCount: totalFieldCount,
          overriddenFieldCount,
          isVirtual: true,
          isMultiRecord: true,
          baseRecords: virtualRecord.baseRecords,
          label: virtualRecord.label,
        };
      } else {
        // Handle single-record virtual records (backward compatibility)

        // For recursive inheritance, we need to find the ultimate base record
        // and use recursive field override resolution
        let ultimateBaseRecord = null;
        let currentRecordId = virtualRecord.base;

        // Walk the chain to find the ultimate base record
        console.log(`Walking inheritance chain for ${recordId}, starting from ${currentRecordId}`);
        while (currentRecordId) {
          console.log(`  Checking ${currentRecordId}`);
          const realRecord = schema.records.find(r => r.record === currentRecordId);
          if (realRecord) {
            console.log(`  Found ultimate base record: ${currentRecordId}`);
            ultimateBaseRecord = realRecord;
            break;
          }

          // Check if it's a virtual record
          const virtualBase = template.virtualRecords?.[currentRecordId];
          console.log(`  Virtual base for ${currentRecordId}:`, virtualBase);
          if (virtualBase?.base) {
            console.log(`  Following virtual inheritance: ${currentRecordId} -> ${virtualBase.base}`);
            currentRecordId = virtualBase.base;
          } else {
            console.log(`  No further inheritance found for ${currentRecordId}`);
            break;
          }
        }

        if (!ultimateBaseRecord) {
          console.warn(`Virtual record ${recordId} cannot find ultimate base record from ${virtualRecord.base}`);
          return null;
        }

        // Use recursive field override resolution to count total overrides
        try {
          const resolvedOverrides = resolveFieldOverridesRecursively(recordId, template);
          const overriddenFieldCount = Object.keys(resolvedOverrides).length;

          return {
            record: recordId,
            fields: ultimateBaseRecord.fields, // Use ultimate base record fields
            enabled: virtualRecord.enabled,
            fieldCount: ultimateBaseRecord.fields.length,
            overriddenFieldCount,
            isVirtual: true,
            baseRecord: virtualRecord.base, // Keep immediate parent for display
            label: virtualRecord.label,
          };
        } catch (error) {
          console.error(`Error resolving overrides for virtual record ${recordId}:`, error);
          return null;
        }
      }
    }).filter(Boolean) as RecordWithMetadata[];

    return [...realRecords, ...virtualRecords];
  }, [schema, template]);

  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) return recordsWithMetadata;

    const query = searchQuery.toLowerCase();
    return recordsWithMetadata.filter(record =>
      record.record.toLowerCase().includes(query) ||
      record.fields.some(field =>
        field.xmlTag.toLowerCase().includes(query) ||
        field.description?.toLowerCase().includes(query) ||
        field.links?.some(link => link.toLowerCase().includes(query))
      )
    );
  }, [recordsWithMetadata, searchQuery]);

  const enabledCount = recordsWithMetadata.filter(r => r.enabled).length;
  const totalOverrides = recordsWithMetadata.reduce((acc, r) => acc + r.overriddenFieldCount, 0);

  if (!schema || !template) {
    return (
      <Box ta="center" py="xl">
        <Text c="dimmed">Loading records...</Text>
      </Box>
    );
  }

  return (
    <Stack gap="xs">
      {/* Search */}
      <TextInput
        placeholder="Search..."
        leftSection={<IconSearch size={14} />}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        size="xs"
      />

      {/* Stats */}
      <Group gap={4}>
        <Badge variant="light" size="xs">
          {enabledCount}/{recordsWithMetadata.length}
        </Badge>
        {totalOverrides > 0 && (
          <Badge variant="light" color="orange" size="xs">
            {totalOverrides} ovr
          </Badge>
        )}
      </Group>

      <Divider />

      {/* Records List */}
      <Stack gap={4} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {filteredRecords.map((record) => (
          <RecordItem
            key={record.record}
            record={record}
            isSelected={selectedRecord === record.record}
            onSelect={() => onRecordSelect(record.record)}
            onToggle={(enabled) => onRecordToggle(record.record, enabled)}
            onConfigureLookupFields={() => {
              const currentFields = template?.recordLookupFields?.[record.record] || [];
              setEditingFields(currentFields);
              setLookupFieldsModal({ open: true, recordName: record.record });
            }}
            onConfigureRecordValidation={() => {
              setPendingValidationRules(template?.recordValidations?.[record.record]);
              setRecordValidationModal({ open: true, recordName: record.record });
            }}
            hasLookupFields={!!template?.recordLookupFields?.[record.record]?.length}
          />
        ))}

        {filteredRecords.length === 0 && (
          <Box ta="center" py="xs">
            <Text c="dimmed" size="xs">
              No matches
            </Text>
          </Box>
        )}
      </Stack>

      {/* Lookup Fields Configuration Modal */}
      <Modal
        opened={lookupFieldsModal.open}
        onClose={() => setLookupFieldsModal({ open: false, recordName: '' })}
        title={`Configure Lookup Fields for ${lookupFieldsModal.recordName}`}
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Specify additional fields to fetch whenever {lookupFieldsModal.recordName} is used as a lookup table.
            These fields will be available for display in lookup dropdowns.
          </Text>

          {editingFields.map((field, index) => (
            <Group key={index} gap="xs">
              <TextInput
                placeholder="Field name (e.g., UDF.CCY)"
                value={field.name}
                onChange={(e) => {
                  const newFields = [...editingFields];
                  newFields[index] = { ...newFields[index], name: e.target.value };
                  setEditingFields(newFields);
                }}
                style={{ flex: 1 }}
              />
              <TextInput
                placeholder="Alias (optional)"
                value={field.alias || ''}
                onChange={(e) => {
                  const newFields = [...editingFields];
                  newFields[index] = { ...newFields[index], alias: e.target.value };
                  setEditingFields(newFields);
                }}
                style={{ flex: 1 }}
              />
              <Button
                size="xs"
                color="red"
                variant="subtle"
                onClick={() => {
                  const newFields = editingFields.filter((_, i) => i !== index);
                  setEditingFields(newFields);
                }}
              >
                Remove
              </Button>
            </Group>
          ))}

          <Button
            size="sm"
            variant="light"
            onClick={() => {
              setEditingFields([...editingFields, { name: '' }]);
            }}
          >
            Add Field
          </Button>

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => setLookupFieldsModal({ open: false, recordName: '' })}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const validFields = editingFields.filter(f => f.name.trim());
                onRecordLookupFieldsChange(lookupFieldsModal.recordName, validFields);
                setLookupFieldsModal({ open: false, recordName: '' });
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Record-Level Validation Modal */}
      <Modal
        opened={recordValidationModal.open}
        onClose={() => setRecordValidationModal({ open: false, recordName: '' })}
        title={`Record-level Validation for ${recordValidationModal.recordName}`}
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Add validation rules for the entire record. You can use templates or create custom expressions.
          </Text>

          <TemplateValidationEditor
            validationRules={pendingValidationRules}
            onValidationChange={(rules) => {
              // Just update local state, don't dispatch event yet
              setPendingValidationRules(rules);
            }}
            templates={validationTemplates}
            fieldName={`Record ${recordValidationModal.recordName}`}
            recordFields={recordsWithMetadata.find(r => r.record === recordValidationModal.recordName)?.fields || []}
            allRecordFields={recordsWithMetadata.flatMap(r =>
              r.fields.map(f => ({ ...f, recordName: r.record }))
            )}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              onClick={() => {
                setRecordValidationModal({ open: false, recordName: '' });
                setPendingValidationRules(undefined);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Only dispatch the event when user explicitly saves
                const detail = { recordName: recordValidationModal.recordName, rules: pendingValidationRules };
                window.dispatchEvent(new CustomEvent('save-record-validations', { detail }));
                setRecordValidationModal({ open: false, recordName: '' });
                setPendingValidationRules(undefined);
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

interface RecordItemProps {
  record: RecordWithMetadata;
  isSelected: boolean;
  onSelect: () => void;
  onToggle: (enabled: boolean) => void;
  onConfigureLookupFields: () => void;
  onConfigureRecordValidation: () => void;
  hasLookupFields: boolean;
  quantityCheckEnabled: boolean;
}

const RecordItem: React.FC<RecordItemProps> = ({
  record,
  isSelected,
  onSelect,
  onToggle,
  onConfigureLookupFields,
  onConfigureRecordValidation,
  hasLookupFields,
  quantityCheckEnabled,
}) => {
  return (
    <Box
      p="xs"
      style={{
        borderRadius: 4,
        border: `1px solid ${isSelected ? 'var(--mantine-color-blue-4)' : 'var(--mantine-color-gray-3)'}`,
        backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : 'transparent',
        cursor: 'pointer',
      }}
      onClick={onSelect}
    >
      <Group gap={4} wrap="nowrap">
        <Checkbox
          checked={record.enabled}
          onChange={(e) => {
            e.stopPropagation();
            onToggle(e.target.checked);
          }}
          size="xs"
        />
        <Box flex={1} style={{ minWidth: 0 }}>
          <Group gap={4} wrap="nowrap">
            {record.isVirtual && !record.isMultiRecord && (
              <IconCopy size={12} color="var(--mantine-color-blue-6)" />
            )}
            {record.isMultiRecord && (
              <IconLayersLinked size={12} color="var(--mantine-color-violet-6)" />
            )}
            <Text size="xs" fw={500} truncate title={record.isVirtual ? record.label : record.record}>
              {record.isVirtual ? record.label : record.record}
            </Text>
          </Group>
          <Group gap={4} mt={2}>
            <Badge variant="dot" size="xs" style={{ paddingLeft: 4, paddingRight: 4 }}>
              {record.fieldCount}
            </Badge>
            {record.overriddenFieldCount > 0 && (
              <Badge variant="dot" color="orange" size="xs" style={{ paddingLeft: 4, paddingRight: 4 }}>
                {record.overriddenFieldCount}
              </Badge>
            )}
            {hasLookupFields && (
              <Badge variant="dot" color="blue" size="xs" style={{ paddingLeft: 4, paddingRight: 4 }}>
                lookup
              </Badge>
            )}
          </Group>
        </Box>
        <Tooltip label="Configure lookup fields">
          <ActionIcon
            size="xs"
            variant="subtle"
            onClick={(e) => {
              e.stopPropagation();
              onConfigureLookupFields();
            }}
          >
            <IconSettings size={10} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Configure record validations">
          <ActionIcon
            size="xs"
            variant="subtle"
            onClick={(e) => {
              e.stopPropagation();
              onConfigureRecordValidation();
            }}
          >
            <IconSettings size={10} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  );
}; 